import type { ReactNode } from 'react'
import { useRef, useState } from 'react'
import { SectionCard } from '../../shared/section-card'
import { useCoachingStore } from '../../store'
import type { ReviewApproach, ReviewComment } from '../contracts'
import { CritiqueFlag } from '../critique-flag'

/**
 * Alternative-approach library (spec §8.5, decision D22) — the approaches
 * authored for the review's own problem.
 *
 * Presentational: the approaches arrive as a prop, read from the store by
 * `coding-review.tsx` and served by `listReviewApproaches`. It used to hold a
 * module constant of three Longest Increasing Subsequence solutions with
 * invented "note" prose, rendered on every coding review whatever the problem
 * was (LG-640). Nothing on screen here is authored by this file now: the chip
 * is `Approach.name`, the block is `Solution.code`, the prose under it is
 * `Solution.explanation`, and the complexity line is `ApproachComplexity` —
 * which is why it renders here while the Complexity section LG-632 deleted
 * (the client's own claimed figures, invented) stays gone.
 *
 * No approaches ⇒ no section, rather than an empty picker. That covers a
 * problem with none authored, a card with no problem behind it, and the
 * moment before the read lands.
 *
 * With exceptions, all of which are "there is something to say here": a
 * comment already anchored to this section, and either of the two reads
 * behind it failing. The comment half is `SectionCard`'s to enforce
 * (LG-821) — unmounting would take the section's own comments and the anchor
 * `review-tab.tsx` places their gutter cards against down with it, so a
 * coach's persisted feedback would disappear from the screen while the
 * queue's counters kept counting it.
 *
 * The failed reads are this section's own and ride along in `hasOwnContent`.
 * There are two of them, and they fail independently: `approaches` and the
 * attachments read behind `attachmentsErrorFor`. Naming only the first is
 * what let an empty problem whose attachments read failed unmount the card
 * and take the sole report of that failure with it. A card carrying nothing
 * but the red banner is the deliberate outcome (LG-821 review): the coach
 * cannot act on it, but a degraded read said out loud beats one swallowed.
 */
export function AlternativeApproaches(props: {
  submissionId: string
  approaches: ReviewApproach[]
  comments?: ReviewComment[]
  /** Why the approaches could not be read, when they could not be. */
  error?: string | null
  anchorRef?: React.Ref<HTMLDivElement>
}): ReactNode {
  const {
    submissionId,
    approaches,
    comments = [],
    error = null,
    anchorRef,
  } = props
  const {
    attachedApproachIdsFor,
    attachmentsErrorFor,
    toggleApproachAttachment,
  } = useCoachingStore().review
  const sectionComments = comments.filter((c) => c.section === 'alternative')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // Held inert while a toggle is in flight, the shape `LifecycleButton` uses.
  // The label only moves once the write and the refetch behind it land, so a
  // second click would re-send a detach and be refused for one that actually
  // succeeded.
  const [pending, setPending] = useState(false)
  // Declared unconditionally (rules of hooks) even though only the picker
  // below uses it, for the roving-tabindex focus moves.
  const chipRefs = useRef<(HTMLButtonElement | null)[]>([])

  // Per approach, not per section (LG-644). The button used to flip one
  // boolean keyed `alternative-approach:${submissionId}` in the shared review
  // UI state — one flag for the whole picker, which could not describe a
  // persisted `ReviewAttachment` row, since that names an approach. The five
  // behavioral and system-design sections that still key that shared map
  // (star answer, HLD diagram, deep dives, alternative stories, suggested
  // resources) keep their local toggles; this section no longer touches it.
  const attachedApproachIds = attachedApproachIdsFor(submissionId)
  // Null means the read has not answered — still loading, or failed. Treating
  // that as "nothing attached" would tick no chip and label the toggle
  // "Attach to review" for an approach that is already attached, and the click
  // it invites would write a duplicate the unique index refuses. So the
  // control is unavailable until the truth is in.
  const attachmentsKnown = attachedApproachIds !== null
  const attachmentsError = attachmentsErrorFor(submissionId)
  // Every source the body renders on its own account, in one place beside the
  // three derivations that feed it — a fourth render branch added below has to
  // pass this line to get on screen at all. `attachmentsError` counts: its
  // banner exists precisely because silence would read as "nothing is
  // attached", and the attachments read fails independently of the approaches
  // read, so an empty problem whose attachments 404 would otherwise swallow it.
  const hasOwnContent =
    approaches.length > 0 || error !== null || attachmentsError !== null

  // Falls back to the head of the catalog's own `(order, id)` ordering — the
  // approach an author put first, not whichever row a query happened to
  // return first. Derived rather than seeded into state so switching to
  // another review's approaches cannot leave a stale id selected.
  //
  // Annotated `| undefined` because it genuinely is: the list is empty
  // whenever a comment or an error is the only reason to mount. Without
  // the annotation `noUncheckedIndexedAccess` is off, so the checker would
  // call the `selected !== undefined` test below redundant and invite someone
  // to delete it.
  const selected: ReviewApproach | undefined =
    approaches.find((a) => a.approachId === selectedId) ?? approaches[0]

  // The button speaks for the approach on screen, so it reads the selection
  // rather than the section.
  const attached =
    selected !== undefined &&
    (attachedApproachIds?.has(selected.approachId) ?? false)

  // The single tab stop, keyed off the *resolved* selection rather than off
  // `selectedId`: that starts null and can hold an id from a previous
  // review's approaches, and either case would give every chip `tabIndex -1`
  // and strand the group out of keyboard reach. `selected` always names a row
  // that is in `approaches` whenever there is one.
  const activeIndex = Math.max(
    approaches.findIndex((a) => a.approachId === selected?.approachId),
    0,
  )

  // WAI-ARIA APG radiogroup, the same shape `components/ui/chip-group.tsx`
  // settled on: arrow keys move the roving tab stop, and per the pattern
  // moving focus also selects — so the selection and the DOM focus target
  // move together.
  const moveTo = (index: number) => {
    const targetIndex = (index + approaches.length) % approaches.length
    const target = approaches[targetIndex]
    if (target === undefined) return
    setSelectedId(target.approachId)
    chipRefs.current[targetIndex]?.focus()
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault()
        moveTo(index + 1)
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault()
        moveTo(index - 1)
        break
      case 'Home':
        event.preventDefault()
        moveTo(0)
        break
      case 'End':
        event.preventDefault()
        moveTo(approaches.length - 1)
        break
      default:
        break
    }
  }

  return (
    <SectionCard
      title="Alternative approach"
      commentCount={sectionComments.length}
      hasContent={hasOwnContent}
      anchorRef={anchorRef}
    >
      {sectionComments.map((c) => (
        <CritiqueFlag key={c.id} comment={c} />
      ))}

      {attachmentsError !== null && (
        // Said out loud for the same reason the approaches error is: silence
        // here would read as "nothing is attached", which is a claim this
        // section cannot currently make. `role="alert"` because the node is
        // inserted when the read fails rather than sitting there empty, and
        // an alert is the one live region announced on insertion
        // (corpus-status.tsx's reasoning) — without it the banner is still
        // swallowed for a screen reader, which is the user it was added for.
        <div
          role="alert"
          className="mb-3 text-xs leading-relaxed text-(--cd-red)"
        >
          {attachmentsError}
        </div>
      )}

      {error !== null && (
        // Said out loud rather than folded into the lane's `status`, which
        // would blank the whole queue over one section — and rather than
        // swallowed, which would show a coach "this problem has no
        // alternatives" when the truth is that nobody managed to ask. Same
        // insert-on-failure lifecycle as the attachments banner above, so the
        // same `role="alert"`.
        <div
          role="alert"
          className="mb-3 text-xs leading-relaxed text-(--cd-red)"
        >
          {error}
        </div>
      )}

      {selected !== undefined && (
        <>
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="text-xs text-(--cd-text-faint)">
              Pick from the approach library to show a different way of solving
              it.
            </div>
            <button
              type="button"
              onClick={async () => {
                setPending(true)
                try {
                  await toggleApproachAttachment(submissionId, selected)
                } finally {
                  setPending(false)
                }
              }}
              // Tri-state, so no binary claim while the read is out: React
              // drops the attribute for `undefined`, and a screen reader in
              // browse mode announces "not pressed" whether or not the control
              // is disabled -- which is the very lie this is here to stop.
              aria-pressed={attachmentsKnown ? attached : undefined}
              disabled={!attachmentsKnown || pending}
              aria-busy={
                pending || (!attachmentsKnown && attachmentsError === null)
              }
              className="shrink-0 rounded-full border px-3.5 py-1 text-xs font-semibold disabled:opacity-50"
              style={
                attached
                  ? {
                      background:
                        'color-mix(in oklab, var(--cd-green) 14%, transparent)',
                      color: 'var(--cd-green)',
                      borderColor:
                        'color-mix(in oklab, var(--cd-green) 40%, transparent)',
                    }
                  : {
                      background: 'transparent',
                      color: 'var(--cd-text-muted)',
                      borderColor: 'var(--cd-border-2)',
                    }
              }
            >
              {/* The hold reason rides in the label rather than a `title`:
                  the button is disabled, so a tooltip is unreachable by
                  keyboard and invisible on touch -- the coach would be left
                  with a greyed-out control and no account of why. */}
              {attachmentsKnown
                ? pending
                  ? attached
                    ? 'Detaching\u2026'
                    : 'Attaching\u2026'
                  : attached
                    ? '\u2713 Attached to review'
                    : 'Attach to review'
                : attachmentsError === null
                  ? 'Checking attachments\u2026'
                  : 'Attach to review'}
            </button>
          </div>

          {/* Radiogroup, not a row of toggles: picking one approach unpicks
              the others, and the selection is the one piece of state this
              control exists to express — so it is announced, not left to
              colour. One tab stop for the whole group; arrows move within it. */}
          <div
            className="mb-3 flex flex-wrap gap-1.5"
            role="radiogroup"
            aria-label="Approach library"
          >
            {approaches.map((a, index) => {
              const active = a.approachId === selected.approachId
              const chipAttached =
                attachedApproachIds?.has(a.approachId) ?? false
              return (
                <button
                  key={a.approachId}
                  ref={(node) => {
                    chipRefs.current[index] = node
                  }}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  // Selection and attachment are two different facts about a
                  // chip, and `aria-checked` is already spent on the first.
                  // Spelling the second into the accessible name is what
                  // keeps the ✓ below from being colour-and-glyph only.
                  aria-label={
                    chipAttached ? `${a.name} (attached to review)` : a.name
                  }
                  tabIndex={index === activeIndex ? 0 : -1}
                  onClick={() => setSelectedId(a.approachId)}
                  onKeyDown={(event) => handleKeyDown(event, index)}
                  className="rounded-full px-3 py-1 text-xs font-medium"
                  style={
                    active
                      ? {
                          background: 'var(--cd-text)',
                          color: 'var(--cd-bg)',
                          border: '1px solid var(--cd-text)',
                        }
                      : {
                          background: 'transparent',
                          color: 'var(--cd-text-muted)',
                          border: '1px solid var(--cd-border-2)',
                        }
                  }
                >
                  {/* Which approaches are attached, visible without clicking
                      through every chip — the whole point of the toggle being
                      per approach rather than per section. */}
                  {chipAttached && (
                    <span aria-hidden="true" className="mr-1">
                      {'\u2713'}
                    </span>
                  )}
                  {a.name}
                </button>
              )
            })}
          </div>

          {selected.code === null ? (
            // The approach is authored, its solution is not \u2014 say so instead
            // of rendering an empty block the coach would read as "no code
            // needed".
            <div className="mb-2.5 rounded-[10px] border border-(--cd-border-2) border-dashed p-3.5 text-xs text-(--cd-text-faint)">
              No solution code is recorded for this approach yet.
            </div>
          ) : (
            <>
              {/* Which language is on screen, because it is not always the one
                  the client wrote in: the reader serves the submission's
                  language and falls back to python per approach. */}
              <div className="mb-1.5 font-mono text-[10px] tracking-wide text-(--cd-text-faint) uppercase">
                {selected.language}
              </div>
              <pre className="mb-2.5 overflow-x-auto rounded-[10px] border border-(--cd-border-2) bg-(--cd-bg) p-3.5 font-mono text-xs leading-relaxed text-(--cd-text-2)">
                {selected.code}
              </pre>
            </>
          )}

          {selected.complexities.map((complexity) => {
            // Both columns are nullable, so the heading is built from whichever
            // are present and skipped entirely when neither is \u2014 an empty
            // heading over an explanation reads as a missing value rather than
            // an unrecorded one.
            const heading = [
              complexity.timeComplexity === null
                ? null
                : `Time ${complexity.timeComplexity}`,
              complexity.spaceComplexity === null
                ? null
                : `Space ${complexity.spaceComplexity}`,
            ]
              .filter((part) => part !== null)
              .join(' \u00b7 ')
            // Keyed by the column each came from, not by its own text: both
            // are free-form authored prose, and an author who wrote the same
            // sentence in both ("Dominated by the sort.") would otherwise
            // collide two siblings on one key and lose a recorded line.
            const explanations = [
              { column: 'time', text: complexity.timeComplexityExplanation },
              { column: 'space', text: complexity.spaceComplexityExplanation },
            ].filter((part) => part.text !== null)
            if (heading === '' && explanations.length === 0) return null
            return (
              <div
                key={complexity.complexityId}
                className="mb-2 text-xs leading-relaxed text-(--cd-text-muted)"
              >
                {/* Which function the figures describe. An approach authored
                    class-style carries one row per method, so unlabelled
                    headings would stack identical-looking lines with no way to
                    tell which is which. Stored kebab-case is what the column
                    holds; nothing here renames it. */}
                <div className="font-mono text-[10px] text-(--cd-text-faint)">
                  {complexity.functionName}
                </div>
                {heading !== '' && (
                  <div className="font-medium text-(--cd-text-2)">
                    {heading}
                  </div>
                )}
                {explanations.map((explanation) => (
                  <div key={`${complexity.complexityId}-${explanation.column}`}>
                    {explanation.text}
                  </div>
                ))}
              </div>
            )
          })}

          {selected.explanation !== null && (
            <div className="text-xs leading-relaxed text-(--cd-text-muted)">
              {selected.explanation}
            </div>
          )}
        </>
      )}
    </SectionCard>
  )
}
